import { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import TourAreaRail from './TourAreaRail';
import type { TourAreaMode } from '../../utils/tourAreas';
import SmartView, { CameraPose, SceneProjectInfo, SmartViewBimItem, SmartViewGuestMeasures, SmartViewHandle, SmartViewInventory, SmartViewLayerItem, SmartViewRailItem } from './SmartView';
import BasemapRail from './BasemapRail';
import SplatViewer, { SplatInventory, SplatViewerHandle } from './SplatViewer';
import DWGViewer from './DWGViewer';
import PDFViewer from './PDFViewer';
import ImageViewer from './ImageViewer';
import BIMViewerV2 from './BIMViewerV2';
import OrthoViewer from './OrthoViewer';
import { PAGE_CONFIG } from './FileListPage';
import { accessOf, NO_ACCESS, sectionAllowed, type FeatureAccess } from '../../utils/access';
import { PROJECT_QUERY, PROJECT_FILES_QUERY } from '../../graphql/queries';
import { useAuth } from '../../contexts/AuthContext';
import { LinkedFile } from '../../utils/linkedFiles';
import { useDocumentFormats } from '../../hooks/useDocumentFormats';
import { documentOpensIn } from '../../utils/fileFormats';
import Modal from '../../components/common/Modal';
import styles from './CombinedView.module.css';
import Icon, { type IconName } from '../../components/common/Icon';

type SectionKey = keyof typeof PAGE_CONFIG;

const SECTION_ORDER: SectionKey[] = ['point-cloud', '2d', '3d', 'documents', 'gaussian', 'orthomosaic'];

interface RailFile {
  id: string;
  originalName: string;
  extension: string;
  status: string;
  type: string;
}

export interface CombinedShareFile {
  id: string;
  originalName: string;
  type: string;
  fileSize: number;
  extension: string;
  status: string;
  panoramaCount?: number;
  srs?: { epsg?: number | null; proj4?: string | null; projected?: boolean | null } | null;
}

interface CombinedViewProps {
  shareToken?: string;
  sharedFiles?: CombinedShareFile[];
  shareProjectId?: string;
  shareProjectName?: string;
  shareAccess?: FeatureAccess;
  guestMeasures?: SmartViewGuestMeasures;
  sharedProject?: SceneProjectInfo;
}

interface TechSelection {
  id: string;
  originalName: string;
  extension: string;
  section: SectionKey;
}

type PcItemKind = 'notes' | 'photos' | 'measurements';

interface PointCloudRailExtras {
  inventory: SmartViewInventory | null;
  onToggleItem: (kind: PcItemKind, id: string, visible: boolean) => void;
  onToggleAllItems: (kind: PcItemKind, visible: boolean) => void;
  onToggleZone: (id: string, active: boolean) => void;
  /** Clicking a row's name opens it in the viewer (fly to / show the note). */
  onFocusItem: (kind: PcItemKind, id: string) => void;
  onFlyToZone: (id: string) => void;
  /** Absent where nothing may be deleted; an item with deletable: false hides its button. */
  onDeleteItem?: (kind: 'notes' | 'measurements', id: string) => void;
  onDeleteZone?: (id: string) => void;
  onGoArea: (key: string) => void;
  onRenameArea: (key: string, name: string) => Promise<void>;
  onAreaMode: (mode: TourAreaMode) => Promise<void>;
  onDrawAreas: () => void;
}

interface SplatRailExtras {
  inventory: SplatInventory | null;
  onSetScene: (url: string) => void;
  onFocusNote: (id: string) => void;
  onToggleNote: (id: string, visible: boolean) => void;
  onToggleAllNotes: (visible: boolean) => void;
}

interface RailSectionProps {
  section: SectionKey;
  files: RailFile[];
  loading: boolean;
  pcVisibility: Record<string, boolean>;
  onTogglePointCloud: (fileId: string, visible: boolean) => void;
  /** Orthomosaics, drawings and splats drawn inside the scene, by file id. */
  layerById: Record<string, SmartViewLayerItem>;
  onToggleLayer: (fileId: string, visible: boolean) => void;
  onOpenAlign: (fileId: string) => void;
  bimById: Record<string, SmartViewBimItem>;
  onToggleBim: (fileId: string, visible: boolean) => void;
  splitFileId: string | null;
  onToggleSplit: (file: TechSelection, open: boolean) => void;
  pc?: PointCloudRailExtras;
  /** The splat that is the scene's base lists its scenes and notes here. */
  /** Rail controls for every splat currently rendered (base or split pane), by file id. */
  splat?: Record<string, SplatRailExtras>;
  /** Notes of the splat / orthomosaic layers that are on in the scene, by file id. */
  sceneNotes?: SceneLayerItemsRail;
  /** Measurements of the orthomosaic layers that are on in the scene, by file id. */
  sceneMeasures?: SceneLayerItemsRail;
}

interface SceneLayerItemsRail {
  byFile: Record<string, SmartViewRailItem[]>;
  onToggle: (id: string, visible: boolean) => void;
  onToggleAll: (fileId: string, visible: boolean) => void;
  onFocus: (id: string) => void;
  onDelete?: (id: string) => void;
}

interface RailGroupProps {
  icon: IconName;
  label: string;
  count: number;
  emptyText: string;
  onShowAll?: () => void;
  onHideAll?: () => void;
  children?: ReactNode;
}

const RailGroup = ({ icon, label, count, emptyText, onShowAll, onHideAll, children }: RailGroupProps) => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div>
      <div
        className={`${styles.railSubHeader} ${styles.railSubHeaderClickable}`}
        onClick={() => setCollapsed((c) => !c)}
      >
        <Icon name={collapsed ? 'chevron_right' : 'expand_more'} style={{ fontSize: 14 }} />
        <Icon name={icon} style={{ fontSize: 13 }} />
        <span className={styles.railSubLabel}>{label}</span>
        <span className={styles.railSectionCount}>{count}</span>
        {onShowAll && count > 0 && (
          <button className={styles.railMiniBtn} title="Show all" onClick={(e) => { e.stopPropagation(); onShowAll(); }}>
            <Icon name="visibility" style={{ fontSize: 13 }} />
          </button>
        )}
        {onHideAll && count > 0 && (
          <button className={styles.railMiniBtn} title="Hide all" onClick={(e) => { e.stopPropagation(); onHideAll(); }}>
            <Icon name="visibility_off" style={{ fontSize: 13 }} />
          </button>
        )}
      </div>
      {collapsed ? null : count === 0 ? <p className={styles.railEmpty}>{emptyText}</p> : children}
    </div>
  );
};

const railDash = <span className={styles.railDash}>—</span>;

const RailSection = ({
  section, files, loading, pcVisibility, onTogglePointCloud, layerById, onToggleLayer, onOpenAlign,
  bimById, onToggleBim, splitFileId, onToggleSplit, pc, splat, sceneNotes, sceneMeasures,
}: RailSectionProps) => {
  const config = PAGE_CONFIG[section];
  const isPointCloud = section === 'point-cloud';
  const readyFiles = files.filter((f) => f.status === 'ready');
  const [collapsed, setCollapsed] = useState(false);

  // Overlay column: what the file does INSIDE the scene. Point clouds and a
  // splat never share it (the viewer hides the clouds when a splat goes on);
  // BIM models, orthomosaics and drawings draw over whichever base is on.
  const overlayControl = (f: RailFile) => {
    const ready = f.status === 'ready';
    if (isPointCloud) {
      return (
        <input
          type="checkbox"
          className={styles.checkbox}
          title="Show in scene"
          disabled={!ready}
          checked={ready && pcVisibility[f.id] !== false}
          onChange={(e) => onTogglePointCloud(f.id, e.target.checked)}
        />
      );
    }
    if (section === '3d') {
      const bim = bimById[f.id];
      if (!bim) return railDash;
      return (
        <input
          type="checkbox"
          className={styles.checkbox}
          title={bim.error ?? (bim.converting ? 'Converting…' : 'Draw over the scene')}
          disabled={!ready}
          checked={bim.visible}
          onChange={(e) => onToggleBim(f.id, e.target.checked)}
        />
      );
    }
    const layer = layerById[f.id];
    if (!layer) return railDash;
    const title = layer.error
      ?? (layer.kind === 'splat' ? 'Show in scene instead of the point clouds'
        : layer.kind === 'ortho' ? 'Drape as the ground in the scene'
          : 'Drape the drawing in the scene');
    return (
      <input
        type="checkbox"
        className={styles.checkbox}
        title={title}
        disabled={!ready}
        checked={layer.visible}
        onChange={(e) => onToggleLayer(f.id, e.target.checked)}
      />
    );
  };

  // Placement (move / rotate / scale / opacity) for anything drawn in the scene.
  const alignControl = (f: RailFile) => {
    const on = section === '3d' ? !!bimById[f.id]?.visible : !!layerById[f.id]?.visible;
    if (!on) return null;
    return (
      <button
        type="button"
        className={styles.railAlignBtn}
        title="Placement: move, rotate, scale, opacity"
        onClick={() => onOpenAlign(f.id)}
      >
        <Icon name="open_with" style={{ fontSize: 14 }} />
      </button>
    );
  };

  const splitControl = (f: RailFile) => {
    if (isPointCloud) return railDash;
    const ready = f.status === 'ready';
    const open = splitFileId === f.id;
    return (
      <button
        type="button"
        className={`${styles.railSplitBtn} ${open ? styles.railSplitBtnActive : ''}`}
        title={open ? 'Close the split pane' : 'Open in the split pane'}
        disabled={!ready}
        onClick={() => onToggleSplit({ id: f.id, originalName: f.originalName, extension: f.extension, section }, !open)}
      >
        <Icon name="vertical_split" style={{ fontSize: 15 }} />
      </button>
    );
  };

  // What a scene layer that is on brings into the scene, listed under its row.
  const layerItemGroup = (f: RailFile, rail: SceneLayerItemsRail | undefined, kind: 'notes' | 'measurements') => {
    const items = rail && layerById[f.id]?.visible ? rail.byFile[f.id] : undefined;
    if (!rail || !items) return null;
    const isNotes = kind === 'notes';
    return (
      <RailGroup
        icon={isNotes ? 'comment' : 'straighten'}
        label={isNotes ? 'Notes' : 'Measurements'}
        count={items.length}
        emptyText={isNotes ? 'No notes' : 'No measurements'}
        onShowAll={() => rail.onToggleAll(f.id, true)}
        onHideAll={() => rail.onToggleAll(f.id, false)}
      >
        {items.map((it) => (
          <div key={it.id} className={`${styles.railRow} ${styles.railSubRow}`}>
            <button
              type="button"
              className={`${styles.railRowName} ${styles.railRowLink}`}
              title={isNotes ? 'Show this note' : 'Fly to this measurement'}
              onClick={() => rail.onFocus(it.id)}
            >
              {it.label}
            </button>
            <span className={styles.railCell}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={it.visible}
                onChange={(e) => rail.onToggle(it.id, e.target.checked)}
              />
            </span>
            <span className={styles.railCell}>
              {rail.onDelete && it.deletable !== false && (
                <button
                  type="button"
                  className={styles.railDeleteBtn}
                  title={isNotes ? 'Delete this note' : 'Delete this measurement'}
                  onClick={() => rail.onDelete?.(it.id)}
                >
                  <Icon name="delete" style={{ fontSize: 14 }} />
                </button>
              )}
            </span>
          </div>
        ))}
      </RailGroup>
    );
  };

  const fileRows = loading && files.length === 0 ? (
    <p className={styles.railEmpty}>Loading…</p>
  ) : files.length === 0 ? (
    <p className={styles.railEmpty}>No files</p>
  ) : (
    files.map((f) => {
      const ready = f.status === 'ready';
      const bim = section === '3d' ? bimById[f.id] : undefined;
      const row = (
        <div key={f.id} className={`${styles.railRow} ${ready ? '' : styles.railRowDisabled} ${isPointCloud ? styles.railSubRow : ''}`}>
          <Icon name={config.icon} className={styles.railRowIcon} />
          <span className={styles.railRowName} title={f.originalName}>{f.originalName}</span>
          {!ready && (
            <span className={`${styles.railChip} ${f.status === 'error' ? styles.railChipError : ''}`}>{f.status}</span>
          )}
          {ready && bim?.converting && <span className={styles.railChip}>converting</span>}
          {ready && bim?.error && <span className={`${styles.railChip} ${styles.railChipError}`} title={bim.error}>error</span>}
          {ready && layerById[f.id]?.loading && <span className={styles.railChip}>loading</span>}
          {ready && ((bim?.visible && bim.georef === false) || (layerById[f.id]?.visible && layerById[f.id]?.georef === false)) && (
            <span className={styles.railChip} title="No georeference — Gather (viewer toolbar) moves it onto the base">local</span>
          )}
          {ready && layerById[f.id]?.error && <span className={`${styles.railChip} ${styles.railChipError}`} title={layerById[f.id].error}>error</span>}
          {ready && alignControl(f)}
          <span className={styles.railCell}>{overlayControl(f)}</span>
          <span className={styles.railCell}>{splitControl(f)}</span>
        </div>
      );
      const sceneNoteGroup = layerItemGroup(f, sceneNotes, 'notes');
      const sceneMeasureGroup = layerItemGroup(f, sceneMeasures, 'measurements');
      const splatExtras = section === 'gaussian' ? splat?.[f.id] : undefined;
      if (!splatExtras) return sceneNoteGroup || sceneMeasureGroup ? <div key={f.id}>{row}{sceneNoteGroup}{sceneMeasureGroup}</div> : row;
      const scenes = splatExtras.inventory?.scenes ?? [];
      const notes = splatExtras.inventory?.notes ?? [];
      return (
        <div key={f.id}>
          {row}
          {sceneNoteGroup}
          {scenes.length > 0 && (
            <RailGroup icon="crop_free" label="Scenes" count={scenes.length} emptyText="">
              {scenes.map((sc) => (
                <div key={sc.url} className={`${styles.railRow} ${styles.railSubRow}`}>
                  <button
                    type="button"
                    className={`${styles.railRowName} ${styles.railRowLink} ${sc.active ? styles.railRowLinkActive : ''}`}
                    title={sc.active ? 'Shown now' : 'Show this scene'}
                    onClick={() => splatExtras.onSetScene(sc.url)}
                  >
                    {sc.label}
                  </button>
                  <span className={styles.railCell}>
                    <input type="radio" className={styles.checkbox} checked={sc.active} onChange={() => splatExtras.onSetScene(sc.url)} />
                  </span>
                  <span className={styles.railCell} />
                </div>
              ))}
            </RailGroup>
          )}
          <RailGroup
            icon="comment"
            label={sceneNoteGroup ? 'Notes · split pane' : 'Notes'}
            count={notes.length}
            emptyText="No notes"
            onShowAll={() => splatExtras.onToggleAllNotes(true)}
            onHideAll={() => splatExtras.onToggleAllNotes(false)}
          >
            {notes.map((n) => (
              <div key={n.id} className={`${styles.railRow} ${styles.railSubRow}`}>
                <button
                  type="button"
                  className={`${styles.railRowName} ${styles.railRowLink}`}
                  title="Show this note"
                  onClick={() => splatExtras.onFocusNote(n.id)}
                >
                  {n.label}
                </button>
                <span className={styles.railCell}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={n.visible}
                    onChange={(e) => splatExtras.onToggleNote(n.id, e.target.checked)}
                  />
                </span>
                <span className={styles.railCell} />
              </div>
            ))}
          </RailGroup>
        </div>
      );
    })
  );

  const deleteItem = pc?.onDeleteItem;
  const itemRows = (kind: PcItemKind) => (pc?.inventory?.[kind] ?? []).map((it) => (
    <div key={it.id} className={`${styles.railRow} ${styles.railSubRow}`}>
      <button
        type="button"
        className={`${styles.railRowName} ${styles.railRowLink}`}
        title={kind === 'notes' ? 'Show this note' : kind === 'photos' ? 'Fly to this station' : 'Fly to this measurement'}
        onClick={() => pc?.onFocusItem(kind, it.id)}
      >
        {it.label}
      </button>
      <span className={styles.railCell}>
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={it.visible}
          onChange={(e) => pc?.onToggleItem(kind, it.id, e.target.checked)}
        />
      </span>
      <span className={styles.railCell}>
        {kind !== 'photos' && deleteItem && it.deletable !== false && (
          <button
            type="button"
            className={styles.railDeleteBtn}
            title={kind === 'notes' ? 'Delete this note' : 'Delete this measurement'}
            onClick={() => deleteItem(kind, it.id)}
          >
            <Icon name="delete" style={{ fontSize: 14 }} />
          </button>
        )}
      </span>
    </div>
  ));

  return (
    <div>
      <div
        className={styles.railSectionHeader}
        onClick={() => setCollapsed((c) => !c)}
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        <Icon name={collapsed ? 'chevron_right' : 'expand_more'} style={{ fontSize: 14 }} />
        <Icon name={config.icon} style={{ fontSize: 14 }} />
        <span className={styles.railSectionLabel}>{config.title}</span>
        <span className={styles.railSectionCount}>{files.length}</span>
      </div>
      {collapsed ? null : !isPointCloud || !pc ? fileRows : (
        <>
          <RailGroup
            icon={config.icon}
            label="Point cloud files"
            count={files.length}
            emptyText="No files"
            onShowAll={() => readyFiles.forEach((f) => onTogglePointCloud(f.id, true))}
            onHideAll={() => readyFiles.forEach((f) => onTogglePointCloud(f.id, false))}
          >
            {fileRows}
          </RailGroup>
          {!readyFiles.some((f) => pcVisibility[f.id] !== false) ? (
            <p className={styles.railEmpty}>Switch a point cloud on to see its notes, photos, measurements and zones.</p>
          ) : (
            <>
              <RailGroup
                icon="comment"
                label="Notes"
                count={pc.inventory?.notes.length ?? 0}
                emptyText="No notes"
                onShowAll={() => pc.onToggleAllItems('notes', true)}
                onHideAll={() => pc.onToggleAllItems('notes', false)}
              >
                {itemRows('notes')}
              </RailGroup>
              {(pc.inventory?.areas.length ?? 0) > 0 && (
                <RailGroup icon="pentagon" label="Tour areas" count={pc.inventory?.areas.length ?? 0} emptyText="">
                  <TourAreaRail
                    areas={pc.inventory?.areas ?? []}
                    canRename={!!pc.inventory?.canRenameAreas}
                    mode={pc.inventory?.areaMode ?? 'gaps'}
                    onMode={pc.onAreaMode}
                    onDraw={pc.onDrawAreas}
                    onGo={pc.onGoArea}
                    onRename={pc.onRenameArea}
                  />
                </RailGroup>
              )}
              <RailGroup
                icon="360"
                label="Photos"
                count={pc.inventory?.photos.length ?? 0}
                emptyText="No photos"
                onShowAll={() => pc.onToggleAllItems('photos', true)}
                onHideAll={() => pc.onToggleAllItems('photos', false)}
              >
                {itemRows('photos')}
              </RailGroup>
              <RailGroup
                icon="straighten"
                label="Measurements"
                count={pc.inventory?.measurements.length ?? 0}
                emptyText="No measurements"
                onShowAll={() => pc.onToggleAllItems('measurements', true)}
                onHideAll={() => pc.onToggleAllItems('measurements', false)}
              >
                {itemRows('measurements')}
              </RailGroup>
              <RailGroup
                icon="highlight_alt"
                label="Focus zones"
                count={pc.inventory?.zones.length ?? 0}
                emptyText="No zones"
              >
                {(pc.inventory?.zones ?? []).map((z) => (
                  <div key={z.id} className={`${styles.railRow} ${styles.railSubRow}`}>
                    <button
                      type="button"
                      className={`${styles.railRowName} ${styles.railRowLink}`}
                      title="Fly to this zone"
                      onClick={() => pc.onFlyToZone(z.id)}
                    >
                      {z.label}
                    </button>
                    <span className={styles.railCell}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        title="Only one zone can be active at a time"
                        checked={z.active}
                        onChange={(e) => pc.onToggleZone(z.id, e.target.checked)}
                      />
                    </span>
                    <span className={styles.railCell}>
                      {pc.onDeleteZone && (
                        <button type="button" className={styles.railDeleteBtn} title="Delete this focus zone" onClick={() => pc.onDeleteZone?.(z.id)}>
                          <Icon name="delete" style={{ fontSize: 14 }} />
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </RailGroup>
            </>
          )}
        </>
      )}
    </div>
  );
};

const CombinedView = ({ shareToken, sharedFiles, shareProjectId, shareProjectName, shareAccess, guestMeasures, sharedProject }: CombinedViewProps = {}) => {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const projectId = shareProjectId ?? urlProjectId;
  const isShare = !!shareToken;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const documentFormats = useDocumentFormats();
  // Signed in: the user's own access. Shared: the project owner's — a type
  // the owner may not use never reaches the rail or the scene.
  const access = useMemo(
    () => (isShare ? (shareAccess ?? NO_ACCESS) : accessOf(user)),
    [isShare, shareAccess, user],
  );
  const gaussianAllowed = access.splat;
  const orthoAllowed = access.ortho;
  const sections = SECTION_ORDER.filter((s) => sectionAllowed(s, access));
  const smartViewRef = useRef<SmartViewHandle>(null);
  const splitSplatRef = useRef<SplatViewerHandle>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => dragCleanupRef.current?.(), []);

  const [pcVisibility, setPcVisibility] = useState<Record<string, boolean>>({});
  const [inventory, setInventory] = useState<SmartViewInventory | null>(null);
  const [splitSplatInventory, setSplitSplatInventory] = useState<SplatInventory | null>(null);
  const [techFile, setTechFile] = useState<TechSelection | null>(null);
  // Entry configuration handed to the viewer once (see the query-param effect).
  const [startLayers, setStartLayers] = useState<string[]>([]);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [split, setSplit] = useState(50);
  const [dragging, setDragging] = useState(false);

  const { data } = useQuery(PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId || isShare,
  });
  const projectName = shareProjectName ?? data?.project?.name;

  const { data: filesData, loading: filesLoading } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, page: 1, limit: 500 },
    skip: !projectId || isShare,
  });

  const files: RailFile[] = useMemo(
    () => (sharedFiles ?? filesData?.projectFiles?.files ?? []) as RailFile[],
    [sharedFiles, filesData],
  );
  const filesBySection = useMemo(() => {
    const map: Partial<Record<SectionKey, RailFile[]>> = {};
    for (const section of SECTION_ORDER) {
      map[section] = files.filter((f) => f.type === PAGE_CONFIG[section].fileType);
    }
    return map;
  }, [files]);
  const filesReady = isShare || (!filesLoading && !!filesData);

  // Stable identity — SmartView keys its viewer setup off the file list.
  const sharedPointClouds = useMemo(
    () => (isShare ? sharedFiles?.filter((f) => f.type === 'point_cloud') : undefined),
    [isShare, sharedFiles],
  );

  const bimById = useMemo(() => {
    const map: Record<string, SmartViewBimItem> = {};
    (inventory?.bim ?? []).forEach((b) => { map[b.id] = b; });
    return map;
  }, [inventory]);
  const layerById = useMemo(() => {
    const map: Record<string, SmartViewLayerItem> = {};
    (inventory?.layers ?? []).forEach((l) => { map[l.id] = l; });
    return map;
  }, [inventory]);
  // Share mode: the viewer cannot query files, so hand it every non-point-cloud
  // file the link exposes (ortho / drawings / splats / BIM).
  const sharedLayerFiles = useMemo(
    () => (isShare ? sharedFiles?.filter((f) => f.type !== 'point_cloud') : undefined),
    [isShare, sharedFiles],
  );

  const togglePointCloud = (fileId: string, visible: boolean) => {
    smartViewRef.current?.setFileVisible(fileId, visible);
    setPcVisibility((prev) => ({ ...prev, [fileId]: visible }));
  };

  const toggleSplit = (file: TechSelection, open: boolean) => {
    setTechFile(open ? file : null);
  };

  // The rail's item checkboxes drive SmartView through its handle; SmartView
  // reports the resulting state back via onInventoryChange, which re-renders
  // the rail — the checkboxes never hold state of their own.
  const togglePcItem = (kind: PcItemKind, id: string, visible: boolean) => {
    const h = smartViewRef.current;
    if (!h) return;
    if (kind === 'notes') h.setNoteVisible(id, visible);
    else if (kind === 'photos') h.setStationVisible(id, visible);
    else h.setMeasurementVisible(id, visible);
  };
  const toggleAllPcItems = (kind: PcItemKind, visible: boolean) => {
    (inventory?.[kind] ?? []).forEach((it) => togglePcItem(kind, it.id, visible));
  };
  const togglePcZone = (id: string, active: boolean) => {
    smartViewRef.current?.setActiveZone(active ? id : null);
  };
  const [pendingDelete, setPendingDelete] = useState<{ kind: 'note' | 'measurement' | 'zone'; id: string } | null>(null);
  const deletePcItem = (kind: 'notes' | 'measurements', id: string) =>
    setPendingDelete({ kind: kind === 'notes' ? 'note' : 'measurement', id });
  const deletePcZone = (id: string) => setPendingDelete({ kind: 'zone', id });
  const deleteSceneNote = (id: string) => setPendingDelete({ kind: 'note', id });
  const deleteSceneMeasurement = (id: string) => setPendingDelete({ kind: 'measurement', id });
  const confirmDelete = () => {
    const h = smartViewRef.current;
    if (h && pendingDelete) {
      if (pendingDelete.kind === 'note') h.deleteNote(pendingDelete.id);
      else if (pendingDelete.kind === 'measurement') h.deleteMeasurement(pendingDelete.id);
      else h.deleteZone(pendingDelete.id);
    }
    setPendingDelete(null);
  };
  const deleteNoun = pendingDelete?.kind === 'zone' ? 'focus zone' : pendingDelete?.kind ?? '';
  const focusPcItem = (kind: PcItemKind, id: string) => {
    const h = smartViewRef.current;
    if (!h) return;
    if (kind === 'notes') h.focusNote(id);
    else if (kind === 'photos') h.flyToStation(id);
    else h.flyToMeasurement(id);
  };
  const toggleBim = (fileId: string, visible: boolean) => {
    smartViewRef.current?.setBimVisible(fileId, visible);
  };
  // A note's linked file opens where the rail would put it: point clouds
  // join the scene, everything else goes to the split pane.
  const openLinkedFile = (file: LinkedFile) => {
    const section = SECTION_ORDER.find((s) => PAGE_CONFIG[s].fileType === file.type);
    if (!section || file.status !== 'ready') return;
    if (section === 'point-cloud') { togglePointCloud(file.id, true); return; }
    setTechFile({ id: file.id, originalName: file.originalName, extension: file.extension, section });
  };
  const splatExtrasFor = (ref: RefObject<SplatViewerHandle>, inv: SplatInventory | null): SplatRailExtras => ({
    inventory: inv,
    onSetScene: (url) => ref.current?.setScene(url),
    onFocusNote: (id) => ref.current?.focusNote(id),
    onToggleNote: (id, visible) => ref.current?.setNoteVisible(id, visible),
    onToggleAllNotes: (visible) => (inv?.notes ?? []).forEach((n) => ref.current?.setNoteVisible(n.id, visible)),
  });
  const splitSplatId = techFile?.section === 'gaussian' ? techFile.id : null;
  const splatRail: Record<string, SplatRailExtras> = {};
  if (splitSplatId) splatRail[splitSplatId] = splatExtrasFor(splitSplatRef, splitSplatInventory);
  const toggleLayer = (fileId: string, visible: boolean) => smartViewRef.current?.setLayerVisible(fileId, visible);
  const openAlign = (fileId: string) => smartViewRef.current?.openAlign(fileId);

  // Entry configuration from the project page's "Open Scene" menu:
  //   ?base=splat      the first ready splat starts on (SmartView reads the same
  //   ?base=ortho      param and boots the clouds hidden) / the first ortho is the ground
  //   ?layers=<ids>    these files start switched on in the scene
  //   ?bim=all         every BIM model starts drawn over the scene
  //   ?split=splat     the first ready splat opens in the split pane
  //   ?right=<fileId>  that file opens in the split pane
  //   ?tour=1          enter the nearest panorama once the scene is up
  const entryApplied = useRef(false);
  useEffect(() => {
    if (entryApplied.current || !filesReady) return;
    entryApplied.current = true;
    const firstSplat = gaussianAllowed ? (filesBySection.gaussian ?? []).find((f) => f.status === 'ready') : undefined;
    const firstOrtho = orthoAllowed ? (filesBySection.orthomosaic ?? []).find((f) => f.status === 'ready') : undefined;
    const base = searchParams.get('base');
    const layers = (searchParams.get('layers') || '').split(',').filter(Boolean);
    if (base === 'splat' && firstSplat) layers.unshift(firstSplat.id);
    if (base === 'ortho' && firstOrtho) layers.unshift(firstOrtho.id);
    if (layers.length) setStartLayers([...new Set(layers)]);
    if (searchParams.get('split') === 'splat' && firstSplat) {
      setTechFile({ id: firstSplat.id, originalName: firstSplat.originalName, extension: firstSplat.extension, section: 'gaussian' });
    }
    const rightId = searchParams.get('right');
    if (rightId) {
      for (const section of sections) {
        if (section === 'point-cloud') continue;
        const f = (filesBySection[section] ?? []).find((x) => x.id === rightId && x.status === 'ready');
        if (f) { setTechFile({ id: f.id, originalName: f.originalName, extension: f.extension, section }); break; }
      }
    }
  }, [filesReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const autoTour = searchParams.get('tour') === '1';
  // ?bim=all: switch every model on once the viewer reports them (one shot).
  const bimAllApplied = useRef(false);
  useEffect(() => {
    if (bimAllApplied.current || searchParams.get('bim') !== 'all') return;
    const items = inventory?.bim ?? [];
    if (!inventory?.loaded || !items.length) return;
    bimAllApplied.current = true;
    items.forEach((b) => { if (!b.visible) smartViewRef.current?.setBimVisible(b.id, true); });
  }, [inventory, searchParams]);

  // Camera pose stream (SmartView iframe → DWG pane). Held in a ref-based hub,
  // not state — poses tick several times a second while navigating and must
  // not re-render the rail + both panes each time.
  const poseHubRef = useRef<{ latest: CameraPose | null; listeners: Set<(p: CameraPose) => void> }>(
    { latest: null, listeners: new Set() },
  );
  const handleCameraPose = useCallback((p: CameraPose) => {
    poseHubRef.current.latest = p;
    poseHubRef.current.listeners.forEach((fn) => fn(p));
  }, []);
  const poseSource = useMemo(() => ({
    getLatest: () => poseHubRef.current.latest,
    subscribe: (fn: (p: CameraPose) => void) => {
      poseHubRef.current.listeners.add(fn);
      return () => { poseHubRef.current.listeners.delete(fn); };
    },
  }), []);

  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault();
    setDragging(true);
    const move = (ev: PointerEvent) => {
      const rect = mainRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplit(Math.min(75, Math.max(25, pct)));
    };
    const up = () => {
      setDragging(false);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
    dragCleanupRef.current = up;
  };

  const renderTechPane = (file: TechSelection) => {
    const ext = (file.extension || '').toLowerCase();
    if (file.section === 'gaussian') return <SplatViewer key={file.id} ref={splitSplatRef} fileId={file.id} projectId={projectId} embedded sceneMode onInventoryChange={setSplitSplatInventory} onOpenLinkedFile={openLinkedFile} />;
    if (file.section === 'orthomosaic') return <OrthoViewer key={file.id} fileId={file.id} embedded />;
    if (ext === 'dwg' || ext === 'dxf') return <DWGViewer key={file.id} fileId={file.id} embedded poseSource={poseSource} />;
    // Documents open where File Formats say; the PDF viewer shows a download-only one as a Download panel.
    if (file.section === 'documents') {
      return documentOpensIn(ext, documentFormats) === 'image'
        ? <ImageViewer key={file.id} fileId={file.id} embedded />
        : <PDFViewer key={file.id} fileId={file.id} embedded />;
    }
    // RCS is the one 3D format still tied to the legacy Autodesk viewer, whose
    // page-wide SDK/CSS globals make it unsafe to embed — it keeps the card.
    if (file.section === '3d' && ext !== 'rcs') return <BIMViewerV2 key={file.id} fileId={file.id} embedded />;
    const cfg = PAGE_CONFIG[file.section];
    // RCS has no self-hosted pipeline — it still opens in the Autodesk viewer.
    const viewerPath = cfg.viewerPath === 'viewer-3d-v2' && ext === 'rcs' ? 'viewer-3d' : cfg.viewerPath;
    return (
      <div className={styles.fallbackCard}>
        <Icon name={cfg.icon} style={{ fontSize: 40 }} />
        <p className={styles.fallbackName} title={file.originalName}>{file.originalName}</p>
        <p className={styles.fallbackType}>{cfg.title}</p>
        {/* Shared links have no routes for the legacy/indoor viewers — card only. */}
        {!isShare && (
          <button
            className={styles.openFullBtn}
            onClick={() => navigate(`/projects/${projectId}/${viewerPath}/${file.id}`)}
          >
            <Icon name="open_in_new" style={{ fontSize: 15 }} />
            Open in full viewer
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <header className={styles.topbar}>
        <button
          className={styles.backBtn}
          onClick={() => navigate(isShare ? `/share/${shareToken}` : `/projects/${projectId}`)}
          title="Back"
        >
          <Icon name="arrow_back" style={{ fontSize: 16 }} />
        </button>
        <div className={styles.brandText}>
          <span className={styles.brandLabel}>SCENE</span>
          <span className={styles.brandTitle}>{projectName ?? 'Project'}</span>
        </div>
      </header>

      <div className={styles.body}>
        {/* Collapsing hides the panel's contents instead of unmounting them, so
            its groups keep their open/closed state. */}
        <aside className={`${styles.rail} ${railCollapsed ? styles.railCollapsed : ''}`}>
          {railCollapsed && (
            <button className={styles.railExpandBtn} onClick={() => setRailCollapsed(false)} title="Expand the file panel">
              <Icon name="left_panel_open" className={styles.railToggleIcon} />
              <span className={styles.railExpandLabel}>Files</span>
            </button>
          )}
          <div hidden={railCollapsed}>
            <div className={styles.railColumns}>
              <button className={styles.railCollapseBtn} onClick={() => setRailCollapsed(true)} title="Collapse the file panel">
                <Icon name="left_panel_close" className={styles.railToggleIcon} />
              </button>
              <span className={styles.railColumnsTitle}>Files</span>
              <span className={styles.railCell} title="Show inside the scene">Overlay</span>
              <span className={styles.railCell} title="Open beside the scene">Split</span>
            </div>
            <BasemapRail
              basemap={inventory?.basemap ?? null}
              onChange={(patch) => smartViewRef.current?.setBasemap(patch)}
              onPlace={() => smartViewRef.current?.openSitePlacement()}
              onSave={() => smartViewRef.current?.saveBasemap()}
              onRevert={() => smartViewRef.current?.revertBasemap()}
              onNudge={(forward, right, turn) => smartViewRef.current?.nudgeBasemap(forward, right, turn)}
              onResetTune={() => smartViewRef.current?.resetBasemapTune()}
            />
            {sections.map((section) => (
              <RailSection
                key={section}
                section={section}
                files={filesBySection[section] ?? []}
                loading={!isShare && filesLoading}
                pcVisibility={pcVisibility}
                onTogglePointCloud={togglePointCloud}
                layerById={layerById}
                onToggleLayer={toggleLayer}
                onOpenAlign={openAlign}
                bimById={bimById}
                onToggleBim={toggleBim}
                splitFileId={techFile?.id ?? null}
                onToggleSplit={toggleSplit}
                pc={section === 'point-cloud' ? {
                  inventory,
                  onToggleItem: togglePcItem,
                  onToggleAllItems: toggleAllPcItems,
                  onToggleZone: togglePcZone,
                  onFocusItem: focusPcItem,
                  onFlyToZone: (id) => smartViewRef.current?.flyToZone(id),
                  onDeleteItem: !isShare || guestMeasures?.pointCloud ? deletePcItem : undefined,
                  onDeleteZone: isShare ? undefined : deletePcZone,
                  onGoArea: (key) => smartViewRef.current?.goToArea(key),
                  onRenameArea: async (key, name) => { await smartViewRef.current?.renameArea(key, name); },
                  onAreaMode: async (mode) => { await smartViewRef.current?.setAreaMode(mode); },
                  onDrawAreas: () => smartViewRef.current?.openAreaEditor(),
                } : undefined}
                splat={section === 'gaussian' ? splatRail : undefined}
                sceneNotes={section === 'gaussian' || section === 'orthomosaic' ? {
                  byFile: inventory?.layerNotes ?? {},
                  onToggle: (id, visible) => smartViewRef.current?.setNoteVisible(id, visible),
                  onToggleAll: (fileId, visible) => (inventory?.layerNotes?.[fileId] ?? []).forEach((n) => smartViewRef.current?.setNoteVisible(n.id, visible)),
                  onFocus: (id) => smartViewRef.current?.focusNote(id),
                  onDelete: isShare ? undefined : deleteSceneNote,
                } : undefined}
                sceneMeasures={section === 'orthomosaic' ? {
                  byFile: inventory?.layerMeasurements ?? {},
                  onToggle: (id, visible) => smartViewRef.current?.setMeasurementVisible(id, visible),
                  onToggleAll: (fileId, visible) => (inventory?.layerMeasurements?.[fileId] ?? []).forEach((m) => smartViewRef.current?.setMeasurementVisible(m.id, visible)),
                  onFocus: (id) => smartViewRef.current?.flyToMeasurement(id),
                  onDelete: !isShare || guestMeasures?.ortho ? deleteSceneMeasurement : undefined,
                } : undefined}
              />
            ))}
          </div>
        </aside>

        <div ref={mainRef} className={`${styles.main} ${dragging ? styles.mainDragging : ''}`}>
          <div
            className={styles.pane}
            style={techFile ? { flex: `0 0 calc(${split}% - 3px)` } : { flex: '1 1 auto' }}
          >
            {(
              <SmartView
                ref={smartViewRef}
                projectId={isShare ? undefined : projectId}
                shareToken={shareToken}
                shareProjectId={isShare ? projectId : undefined}
                sharedFiles={sharedPointClouds}
                sharedLayerFiles={sharedLayerFiles}
                startLayers={startLayers}
                embedded
                access={access}
                guestMeasures={guestMeasures}
                sharedProject={sharedProject}
                autoTour={autoTour}
                onOpenLinkedFile={openLinkedFile}
                onVisibilityChange={(fileId, visible) => setPcVisibility((prev) => ({ ...prev, [fileId]: visible }))}
                onVisibilityInit={(vis) => setPcVisibility((prev) => ({ ...vis, ...prev }))}
                onInventoryChange={setInventory}
                onCameraPose={handleCameraPose}
              />
            )}
          </div>
          {techFile && (
            <>
              <div className={styles.dividerBar} onPointerDown={startDrag} />
              <div className={`${styles.pane} ${styles.paneFlex}`}>
                {renderTechPane(techFile)}
              </div>
            </>
          )}
        </div>
      </div>
      <Modal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title={`Delete ${deleteNoun}`}
        footer={
          <>
            <button onClick={() => setPendingDelete(null)} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">Cancel</button>
            <button onClick={confirmDelete} className="px-5 py-2 rounded-lg text-sm font-medium bg-error text-white hover:opacity-90">Delete</button>
          </>
        }
      >
        <p className="text-sm text-on-surface-variant">
          Are you sure you want to delete this {deleteNoun}? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default CombinedView;
